import api from "./api";
import { resolveRoleName } from "../utils/auth";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

const normalizeRoleKey = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

const getCurrentAuthUser = () => {
  try {
    const raw = sessionStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  try {
    const raw = localStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  return null;
};

async function getCurrentUserRoleKey() {
  const user = getCurrentAuthUser();
  if (!user) return "";
  const roleName = await resolveRoleName(user);
  return normalizeRoleKey(roleName);
}

const extractJobArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const toQuery = ({ page, limit, search } = {}) => {
  const query = new URLSearchParams();
  if (page != null) query.append("page", String(page));
  if (limit != null) query.append("limit", String(limit));
  if (String(search || "").trim()) {
    query.append("search", String(search).trim());
  }
  return query.toString();
};

const extractMeta = (payload, page = DEFAULT_PAGE, limit = DEFAULT_LIMIT) => {
  if (payload?.meta && typeof payload.meta === "object") {
    return {
      total: Number(payload.meta.total) || 0,
      page: Number(payload.meta.page) || Number(page) || DEFAULT_PAGE,
      limit: Number(payload.meta.limit) || Number(limit) || DEFAULT_LIMIT,
      totalPages: Number(payload.meta.totalPages) || 1,
    };
  }
  const data = extractJobArray(payload);
  return {
    total: data.length,
    page: Number(page) || DEFAULT_PAGE,
    limit: Number(limit) || DEFAULT_LIMIT,
    totalPages: 1,
  };
};

// Jobs service
export const getJobs = async (options = {}) => {
  const res = await api.get("/job", options);
  return extractJobArray(res).map(normalizeJob);
};

export const getJobsPaginated = async ({
  page = DEFAULT_PAGE,
  limit = DEFAULT_LIMIT,
  search = "",
} = {}) => {
  const qs = toQuery({ page, limit, search });
  const roleKey = await getCurrentUserRoleKey();
  const endpoint =
    roleKey === "SUPERADMIN" ? "/job" : "/hierarchical-view/relation/job/bulk";

  const res = await api.get(`${endpoint}${qs ? `?${qs}` : ""}`);

  return {
    data: extractJobArray(res).map(normalizeJob),
    meta: extractMeta(res, page, limit),
  };
};

export const getUnassignedJobsByCompany = async (companyId) => {
  if (!companyId) return [];
  const res = await api.get(
    `/job/unassigned?companyId=${encodeURIComponent(companyId)}`,
  );
  const arr = Array.isArray(res) ? res : [];
  return arr
    .map((j) => ({
      id: Number(j?.job_id ?? j?.id),
      label: String(j?.name || "").trim(),
    }))
    .filter((j) => Number.isInteger(j.id) && j.id > 0);
};

function buildRaciPayload(raci) {
  if (!Array.isArray(raci) || raci.length === 0) return undefined;
  return raci.map((r) => ({
    task_id: Number(r.task_id),
    role: r.role ?? null,
    time_allocation_percentage:
      r.time_allocation_percentage != null
        ? Number(r.time_allocation_percentage)
        : null,
  }));
}

export const updateJob = async (id, payload) => {
  if (!id) throw new Error("Job id is required");
  const body = {
    jobCode: String(payload.jobCode || payload.job_code || "").trim(),
    name: String(payload.name || "").trim(),
    description: String(payload.description || ""),
    function_id:
      payload.function_id != null ? Number(payload.function_id) : null,
    company_id: payload.company_id != null ? Number(payload.company_id) : null,
    job_category_id:
      payload.category_id != null ? Number(payload.category_id) : null,
    job_status_id: payload.status_id != null ? Number(payload.status_id) : null,
    hourlyRate: payload.hourlyRate != null ? Number(payload.hourlyRate) : null,
    currencyType: payload.currencyType || "USD",
    maxHoursPerDay:
      payload.maxHoursPerDay != null ? Number(payload.maxHoursPerDay) : null,
    staff_count:
      payload.staff_count != null ? Number(payload.staff_count) : null,
    days_per_week:
      payload.daysPerWeek != null ?
        Number(payload.daysPerWeek)
      : payload.days_per_week != null ? Number(payload.days_per_week) : null,
    capacity_buffer:
      payload.capacityBuffer != null ?
        Number(payload.capacityBuffer)
      : payload.capacity_buffer != null ? Number(payload.capacity_buffer) : null,
    jobLevel: payload.jobLevel || payload.job_level_name || "",
    // parent_job_id: null clears the parent; key omitted entirely if not provided
    ...(payload.parent_job_id !== undefined && {
      parent_job_id:
        payload.parent_job_id != null ? Number(payload.parent_job_id) : null,
    }),
    skills: Array.isArray(payload.skills) ? payload.skills : [],
    raci: buildRaciPayload(payload.raci),
  };
  return api.patch(`/job/${id}`, body);
};

export const createJob = async (payload) => {
  const body = {
    jobCode: String(payload.jobCode || payload.job_code || "").trim(),
    name: String(payload.name || "").trim(),
    description: String(payload.description || ""),
    function_id:
      payload.function_id != null ? Number(payload.function_id) : null,
    company_id: payload.company_id != null ? Number(payload.company_id) : null,
    job_category_id:
      payload.category_id != null ? Number(payload.category_id) : null,
    job_status_id: payload.status_id != null ? Number(payload.status_id) : null,
    hourlyRate: payload.hourlyRate != null ? Number(payload.hourlyRate) : null,
    currencyType: payload.currencyType || "USD",
    maxHoursPerDay:
      payload.maxHoursPerDay != null ? Number(payload.maxHoursPerDay) : null,
    staff_count:
      payload.staff_count != null ? Number(payload.staff_count) : null,
    days_per_week:
      payload.daysPerWeek != null ?
        Number(payload.daysPerWeek)
      : payload.days_per_week != null ? Number(payload.days_per_week) : null,
    capacity_buffer:
      payload.capacityBuffer != null ?
        Number(payload.capacityBuffer)
      : payload.capacity_buffer != null ? Number(payload.capacity_buffer) : null,
    // API schema requires string level and simplified skills
    jobLevel: payload.jobLevel || payload.job_level_name || "",
    skills: Array.isArray(payload.skills) ? payload.skills : [],
    raci: buildRaciPayload(payload.raci),
  };
  const res = await api.post("/job", body);
  return res;
};

export const deleteJob = async (id) => {
  if (!id) throw new Error("Job id is required");
  return api.delete(`/job/${id}`);
};

// In-flight coalescing for job-with-relations
const _pendingJobWithRelations = new Map(); // id -> Promise

export const getJobWithRelations = async (id) => {
  if (!id) throw new Error("Job id is required");
  const key = String(id);
  if (_pendingJobWithRelations.has(key))
    return _pendingJobWithRelations.get(key);
  const p = (async () => {
    const res = await api.get(`/job/${encodeURIComponent(id)}/with-relations`);
    if (Array.isArray(res)) return res[0] || null;
    return res || null;
  })().finally(() => {
    _pendingJobWithRelations.delete(key);
  });
  _pendingJobWithRelations.set(key, p);
  return p;
};

// GET /job/{id} — the plain record, which is where `hourlyRate`, `currencyType`
// and `staff_count` live. `getJobWithRelations` hits a different endpoint with a
// different shape, so this stays separate rather than overloading it.
const _pendingJobById = new Map(); // id -> Promise

export const getJobById = async (id) => {
  if (!id) throw new Error("Job id is required");
  const key = String(id);
  if (_pendingJobById.has(key)) return _pendingJobById.get(key);
  const p = (async () => {
    const res = await api.get(`/job/${encodeURIComponent(id)}`);
    if (Array.isArray(res)) return res[0] || null;
    return res || null;
  })().finally(() => {
    _pendingJobById.delete(key);
  });
  _pendingJobById.set(key, p);
  return p;
};

// Dedicated Job Workload endpoint (replaces with-relations for the workload page)
const _pendingJobWorkload = new Map(); // id -> Promise

export const getJobWorkload = async (id) => {
  if (!id) throw new Error("Job id is required");
  const key = String(id);
  if (_pendingJobWorkload.has(key)) return _pendingJobWorkload.get(key);
  const p = (async () => {
    const res = await api.get(`/workload/${encodeURIComponent(id)}`);
    if (Array.isArray(res)) return res[0] || null;
    return res || null;
  })().finally(() => {
    _pendingJobWorkload.delete(key);
  });
  _pendingJobWorkload.set(key, p);
  return p;
};

function normalizeJob(j) {
  if (!j || typeof j !== "object") return j;
  const normalized = {
    ...j,
    job_id: j.job_id ?? j.id ?? null,
    jobCode: j.jobCode ?? j.job_code ?? "",
    name: j.name ?? "",
    function_id:
      j.function_id ?? j.functionId ?? j.Function?.function_id ?? null,
    company_id: j.company_id ?? j.companyId ?? j.company?.company_id ?? null,
    category_id:
      j.category_id ??
      j.categoryId ??
      j.job_category_id ??
      j.jobCategoryId ??
      j.category?.id ??
      null,
    status_id:
      j.status_id ??
      j.statusId ??
      j.job_status_id ??
      j.jobStatusId ??
      j.status?.id ??
      null,
    function: j.function ?? j.Function ?? null,
    function_name:
      j.function_name ?? j.function?.name ?? j.Function?.name ?? "",
    company_name: j.company_name ?? j.company?.name ?? "",
    category_name:
      j.category_name ??
      j.category?.name ??
      j.category?.description ??
      j.jobCategory?.name ??
      j.jobCategory?.description ??
      "",
    status_name:
      j.status_name ??
      j.status?.name ??
      j.status?.description ??
      j.jobStatus?.name ??
      j.jobStatus?.description ??
      "",
    createdAt: j.createdAt ?? j.created_at ?? null,
    updatedAt: j.updatedAt ?? j.updated_at ?? null,
    days_per_week: j.days_per_week ?? j.daysPerWeek ?? null,
    capacity_buffer: j.capacity_buffer ?? j.capacityBuffer ?? null,
  };
  return normalized;
}

// In-flight request coalescing for bulk jobs
const _pendingJobsBulk = new Map(); // key -> Promise

export const getJobsByUsersBulk = async (userIds) => {
  const ids =
    Array.isArray(userIds) ?
      userIds.map((n) => Number(n)).filter((n) => Number.isInteger(n))
    : [];
  if (ids.length === 0) return [];
  const key = ids
    .slice()
    .sort((a, b) => a - b)
    .join(",");
  if (_pendingJobsBulk.has(key)) {
    return _pendingJobsBulk.get(key);
  }
  const body = { userIds: ids };
  const inflight = (async () => {
    const res = await api.post("/hierarchical-view/relation/job/bulk", body);
    const results =
      Array.isArray(res?.results) ? res.results
      : Array.isArray(res) ? res
      : [];
    const coll = [];
    for (const r of results) {
      const data = r?.data || {};
      const a = Array.isArray(data.associatedJobs) ? data.associatedJobs : [];
      const c = Array.isArray(data.createdJobs) ? data.createdJobs : [];
      coll.push(...a, ...c);
    }
    const byId = new Map();
    coll.forEach((j) => {
      const nj = normalizeJob(j);
      const k = String(nj.job_id ?? "");
      if (!byId.has(k)) byId.set(k, nj);
    });
    return Array.from(byId.values());
  })().finally(() => {
    // cleanup when settled
    _pendingJobsBulk.delete(key);
  });
  _pendingJobsBulk.set(key, inflight);
  return inflight;
};

// Get tasks linked to a specific job
export const getTasksByJobId = async (jobId) => {
  if (!jobId) throw new Error("Job id is required");
  try {
    // Get job with relations and extract tasks from taskJobs or job_tasks
    const job = await getJobWithRelations(jobId);
    if (!job) return [];
    // Normalize various backend shapes for task-job relationship
    const taskJobs =
      job.taskJobs || job.task_jobs || job.jobTasks || job.job_tasks || [];
    return Array.isArray(taskJobs) ?
        taskJobs.map((tj) => tj?.task || tj).filter(Boolean)
      : [];
  } catch (e) {
    console.error("Failed to get tasks for job:", e);
    return [];
  }
};

// Get jobs by company ID
export const getJobsByCompanyId = async (companyId) => {
  if (!companyId) throw new Error("Company ID is required");
  try {
    const res = await api.get(`/job/company/${encodeURIComponent(companyId)}`);
    const arr =
      Array.isArray(res) ? res
      : Array.isArray(res?.data) ? res.data
      : [];
    return arr.map(normalizeJob);
  } catch (e) {
    try {
      const fallback = await api.get(
        `/job?companyId=${encodeURIComponent(companyId)}`,
        { suppressErrorPage: true }, // GET /job is SUPER_ADMIN-only
      );
      const arr =
        Array.isArray(fallback) ? fallback
        : Array.isArray(fallback?.data) ? fallback.data
        : [];
      return arr.map(normalizeJob);
    } catch (err) {
      console.error("Failed to get jobs by company:", err);
      return [];
    }
  }
};

const normalizeJobInformation = (job) => {
  if (!job || typeof job !== "object") return null;

  const jobId = job.job_id ?? job.jobId ?? job.id ?? null;
  const jobName = String(job.job_name ?? job.jobName ?? job.name ?? "").trim();
  const jobCode = String(job.job_code ?? job.jobCode ?? "").trim();
  const companyId =
    job.company_id ??
    job.companyId ??
    job.company?.company_id ??
    job.company?.id ??
    null;
  const companyName = String(
    job.company_name ?? job.companyName ?? job.company?.name ?? "",
  ).trim();

  return {
    ...job,
    job_id: jobId,
    jobId,
    job_name: jobName,
    jobName,
    job_code: jobCode,
    jobCode,
    company_id: companyId,
    companyId,
    company_name: companyName,
    companyName,
  };
};

export const generateJobDescription = async ({ jobName, tasks }) => {
  const res = await api.post('/job/generate-description', { jobName, tasks });
  if (!res || typeof res.description !== 'string') {
    throw new Error('Unexpected response from description generation');
  }
  return res.description;
};

export const getJobInformation = async () => {
  const res = await api.get("/job/information");
  const arr =
    Array.isArray(res) ? res
    : Array.isArray(res?.data) ? res.data
    : [];
  return arr.map(normalizeJobInformation).filter(Boolean);
};
